import '../../css/CreateGraphBox.css';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import { useState } from 'react';
import { createGraph, createWalletTemplate } from '../../api/RequestUtils';
import { useGlobalStateValue } from '../../context/GlobalStateProvider';
import ClipLoader from 'react-spinners/ClipLoader';
import { actionTypes } from '../../context/globalReducer';

export default function CreateCustomGraphStep4({ graphConfiguration, onUpdateGraphConfig, gotoBack, gotoBegin }) {

    // Context
    const [{ userJWTCookie }, dispatch] = useGlobalStateValue();

    const [isCumulative, setIsCumulative] = useState(graphConfiguration.customGraphSettings.visualizationSettings.cumulative);
    const [isGroupByCategory, setIsGroupByCategory] = useState(graphConfiguration.customGraphSettings.visualizationSettings.groupByCategory);

    const closeCreateGraphBox = () => {
        dispatch({
            type: actionTypes.CLOSE_ACTIVE_BOX
        })
    }

    const onBackButton = () => {
        if (graphConfiguration.customGraphSettings.dataSettings.source === 'SAVINGS') {
            gotoBegin()
        } else {
            gotoBack()
        }
    }

    const createPendingGraphWithConfiguration = (graphConfig) => {
        const updatedConfig = {
            ...graphConfig,
            graphCreationStatus: "PENDING",
            id: crypto.randomUUID(), // Autogenerate a random id just to be able to reorder it while it loads
        };
        return {
            graphConfiguration: updatedConfig,
            graphData: null
        }
    }

    const handleCreateGraph = async () => {
        let pendingGraphConfiguration = null;
        try {
            closeCreateGraphBox()
            pendingGraphConfiguration = createPendingGraphWithConfiguration(graphConfiguration);
            dispatch({
                type: actionTypes.APPEND_GRAPH,
                value: pendingGraphConfiguration
            })
            const apiResponse = await createGraph(userJWTCookie, graphConfiguration);
            // Override pending graph that just got created
            if (apiResponse) {
                dispatch({
                    type: actionTypes.UPDATE_GRAPH,
                    payload: {
                        id: pendingGraphConfiguration.graphConfiguration.id,
                        data: apiResponse
                    }
                })
            }
        } catch (error) {
            dispatch({
                type: actionTypes.DELETE_GRAPH,
                value: pendingGraphConfiguration.graphConfiguration.id
            });
        } finally { }
    }

    const handleSelectedVisualizationType = (type) => {
        const vis = graphConfiguration.customGraphSettings.visualizationSettings;
        const source = graphConfiguration.customGraphSettings.dataSettings.source;
        const isGrouped = vis.groupByCategory || vis.groupByIncomeBankAccounts || vis.groupByIncomeSources;

        let groupingOverride = {};
        if (type === 'FREQUENCY' && !isGrouped) {
            if (source === 'INCOMES') {
                groupingOverride = { groupByIncomeBankAccounts: true };
            } else {
                groupingOverride = { groupByCategory: true };
                setIsGroupByCategory(true);
            }
        }

        onUpdateGraphConfig({
            customGraphSettings: {
                ...graphConfiguration.customGraphSettings,
                visualizationSettings: {
                    ...vis,
                    type: type,
                    ...groupingOverride
                }
            }
        })
    }

    const handleSelectedGroupByTime = (groupByTime) => {
        onUpdateGraphConfig({
            customGraphSettings: {
                ...graphConfiguration.customGraphSettings,
                visualizationSettings: {
                    ...graphConfiguration.customGraphSettings.visualizationSettings,
                    groupByTime: groupByTime
                }
            }
        })
    }

    const handleSelectedGroupByIncomeBankAccountsCategory = () => {
        if (graphConfiguration.customGraphSettings.visualizationSettings.groupByIncomeBankAccounts === true) {
            if (graphConfiguration.customGraphSettings.visualizationSettings.type === 'FREQUENCY') return;
            onUpdateGraphConfig({
                customGraphSettings: {
                    ...graphConfiguration.customGraphSettings,
                    visualizationSettings: {
                        ...graphConfiguration.customGraphSettings.visualizationSettings,
                        groupByIncomeBankAccounts: false
                    }
                }
            })
        } else {
            onUpdateGraphConfig({
                customGraphSettings: {
                    ...graphConfiguration.customGraphSettings,
                    visualizationSettings: {
                        ...graphConfiguration.customGraphSettings.visualizationSettings,
                        groupByIncomeBankAccounts: true,
                        groupByIncomeSources: false
                    }
                }
            })
        }
    }

    const handleSelectedGroupByIncomeSourcesCategory = () => {
        if (graphConfiguration.customGraphSettings.visualizationSettings.groupByIncomeSources === true) {
            if (graphConfiguration.customGraphSettings.visualizationSettings.type === 'FREQUENCY') return;
            onUpdateGraphConfig({
                customGraphSettings: {
                    ...graphConfiguration.customGraphSettings,
                    visualizationSettings: {
                        ...graphConfiguration.customGraphSettings.visualizationSettings,
                        groupByIncomeSources: false
                    }
                }
            })
        } else {
            onUpdateGraphConfig({
                customGraphSettings: {
                    ...graphConfiguration.customGraphSettings,
                    visualizationSettings: {
                        ...graphConfiguration.customGraphSettings.visualizationSettings,
                        groupByIncomeSources: true,
                        groupByIncomeBankAccounts: false
                    }
                }
            })
        }
    }

    const handleSelectedGroupByCategory = () => {
        if (isGroupByCategory && graphConfiguration.customGraphSettings.visualizationSettings.type === 'FREQUENCY') return;
        var newValue = !isGroupByCategory;
        setIsGroupByCategory(newValue);
        onUpdateGraphConfig({
            customGraphSettings: {
                ...graphConfiguration.customGraphSettings,
                visualizationSettings: {
                    ...graphConfiguration.customGraphSettings.visualizationSettings,
                    groupByCategory: newValue
                }
            }
        })
    }

    const handleSelectedCumulative = () => {
        var newValue = !isCumulative;
        setIsCumulative(newValue);
        onUpdateGraphConfig({
            customGraphSettings: {
                ...graphConfiguration.customGraphSettings,
                visualizationSettings: {
                    ...graphConfiguration.customGraphSettings.visualizationSettings,
                    cumulative: newValue
                }
            }
        })
    }

    const renderStep4Heading = () => {
        if (graphConfiguration.customGraphSettings.dataSettings.source === "EXPENSES") {
            return (
                <div className='creategraphbox__heading last_step'>
                    <h2>Expenses</h2>
                    <TrendingDownRoundedIcon fontSize='medium' />
                </div>
            );
        } else if (graphConfiguration.customGraphSettings.dataSettings.source === "INCOMES") {
            return (
                <div className='creategraphbox__heading last_step'>
                    <h2>Incomes</h2>
                    <AttachMoneyRoundedIcon fontSize='medium' />
                </div>
            );
        } else {
            return (
                <div className='creategraphbox__heading last_step'>
                    <h2>Savings</h2>
                    <TrendingUpRoundedIcon fontSize='medium' />
                </div>
            );
        }
    }

    const renderLineChartText = () => {
        if ((graphConfiguration.customGraphSettings.dataSettings.source === "EXPENSES" && graphConfiguration.customGraphSettings.visualizationSettings.groupByCategory === true) || (graphConfiguration.customGraphSettings.dataSettings.source === "INCOMES" && graphConfiguration.customGraphSettings.visualizationSettings.groupByIncomeBankAccounts === true || graphConfiguration.customGraphSettings.visualizationSettings.groupByIncomeSources === true)) {
            return (
                <p>Multiline chart</p>
            );
        } else {
            return (
                <p>Line chart</p>
            );
        }
    }

    const renderBarChartText = () => {
        if ((graphConfiguration.customGraphSettings.dataSettings.source === "EXPENSES" && graphConfiguration.customGraphSettings.visualizationSettings.groupByCategory === true) || (graphConfiguration.customGraphSettings.dataSettings.source === "INCOMES" && graphConfiguration.customGraphSettings.visualizationSettings.groupByIncomeBankAccounts === true || graphConfiguration.customGraphSettings.visualizationSettings.groupByIncomeSources === true)) {
            return (
                <p>Multibar chart</p>
            );
        } else {
            return (
                <p>Bar chart</p>
            );
        }
    }

    const renderHeatChartText = () => {
        return (
            <p>Heat chart</p>
        );
    }

    const renderFrequencyChartText = () => {
        return (
            <p>Frequency chart</p>
        );
    }

    const renderVisualizationButtons = () => {
        return (
            <div className='creategraphbox__step__bigbuttons scrollable'>
                <button
                    className={`${graphConfiguration.customGraphSettings.visualizationSettings.type === 'LINE' ? 'selected' : 'not_selected'} small`}
                    onClick={() => handleSelectedVisualizationType('LINE')}
                >
                    {renderLineChartText()}
                </button>
                <button
                    className={`${graphConfiguration.customGraphSettings.visualizationSettings.type === 'BAR' ? 'selected' : 'not_selected'} small`}
                    onClick={() => handleSelectedVisualizationType('BAR')}
                >
                    {renderBarChartText()}
                </button>
                <button
                    className={`${graphConfiguration.customGraphSettings.visualizationSettings.type === 'HEAT' ? 'selected' : 'not_selected'} small`}
                    onClick={() => handleSelectedVisualizationType('HEAT')}
                >
                    {renderHeatChartText()}
                </button>
                {graphConfiguration.customGraphSettings.dataSettings.source !== 'SAVINGS' && (
                    <button
                        className={`${graphConfiguration.customGraphSettings.visualizationSettings.type === 'FREQUENCY' ? 'selected' : 'not_selected'} small`}
                        onClick={() => handleSelectedVisualizationType('FREQUENCY')}
                    >
                        {renderFrequencyChartText()}
                    </button>
                )}
            </div>
        );
    }

    const renderGroupByTimeButtons = () => {
        return (
            <div className='creategraphbox__step__bigbuttons scrollable'>
                <button
                    className={`${graphConfiguration.customGraphSettings.visualizationSettings.groupByTime === 'DAY' ? 'selected' : 'not_selected'} small`}
                    onClick={() => handleSelectedGroupByTime('DAY')}
                >
                    <p>Day</p>
                </button>
                <button
                    className={`${graphConfiguration.customGraphSettings.visualizationSettings.groupByTime === 'WEEK' ? 'selected' : 'not_selected'} small`}
                    onClick={() => handleSelectedGroupByTime('WEEK')}
                >
                    <p>Week</p>
                </button>
                <button
                    className={`${graphConfiguration.customGraphSettings.visualizationSettings.groupByTime === 'MONTH' ? 'selected' : 'not_selected'} small`}
                    onClick={() => handleSelectedGroupByTime('MONTH')}
                >
                    <p>Month</p>
                </button>
                <button
                    className={`${graphConfiguration.customGraphSettings.visualizationSettings.groupByTime === 'YEAR' ? 'selected' : 'not_selected'} small`}
                    onClick={() => handleSelectedGroupByTime('YEAR')}
                >
                    <p>Year</p>
                </button>
            </div>
        );
    }

    const renderGroupByCategoriesButtons = () => {
        if (graphConfiguration.customGraphSettings.dataSettings.source === 'EXPENSES') {
            return (
                <button
                    className={`${isGroupByCategory === true ? 'selected' : 'not_selected'} small`}
                    onClick={handleSelectedGroupByCategory}
                >
                    <p>Group by category</p>
                </button>
            );
        } else if (graphConfiguration.customGraphSettings.dataSettings.source === 'INCOMES') {
            return (
                <>
                    <button
                        className={`${graphConfiguration.customGraphSettings.visualizationSettings.groupByIncomeBankAccounts === true ? 'selected' : 'not_selected'} small`}
                        onClick={handleSelectedGroupByIncomeBankAccountsCategory}
                    >
                        <p>Group by bank account</p>
                    </button >
                    <button
                        className={`${graphConfiguration.customGraphSettings.visualizationSettings.groupByIncomeSources === true ? 'selected' : 'not_selected'} small`}
                        onClick={handleSelectedGroupByIncomeSourcesCategory}
                    >
                        <p>Group by income source</p>
                    </button>
                </>
            );
        } else {
            return (<></>);
        }
    }

    const renderOnOffButtons = () => {
        return (
            <div className='creategraphbox__step__bigbuttons notitle'>
                {renderGroupByCategoriesButtons()}
                <button
                    className={`${isCumulative === true ? 'selected' : 'not_selected'} small`}
                    onClick={handleSelectedCumulative}
                >
                    <p>Cumulative results</p>
                </button>
            </div>
        );
    }

    const renderStep4Content = () => {
        return (
            <>
                <div className='creategraphbox__stepgraycontainer'>
                    <h2>Visualization</h2>
                    {renderVisualizationButtons()}
                </div>
                <div className='creategraphbox__stepgraycontainer'>
                    <h2>Group by time</h2>
                    {renderGroupByTimeButtons()}
                </div>
                <div className='creategraphbox__stepgraycontainer'>
                    {renderOnOffButtons()} {/* Group by category and Cumulative results buttons */}
                </div>
            </>
        );
    }

    return (
        <div className='creategraphbox__stepcontainer'>
            <div className='creategraphbox__stepcontent'>
                {renderStep4Heading()}
                {renderStep4Content()}
            </div>
            <div className='creategraphbox__arrows'>
                <button className='creategraphbox__button back' onClick={onBackButton} disabled={false}>
                    Back
                </button>
                <button className='creategraphbox__button next create_graph' onClick={handleCreateGraph} disabled={false}>
                    Create chart
                </button>
            </div>
        </div>
    );
}
